import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit, Trash2, X, Save, Loader2, Tag, Clock } from 'lucide-react';
import api from '../api/axios';
import { useToast } from './Toast';
import { formatPrix, currencySymbol, findTarif } from '../utils/tarifs';

const DURATION_LABELS = {
  15: '15 min', 30: '30 min', 45: '45 min',
  60: '1 h', 90: '1h30', 120: '2 h',
  150: '2h30', 180: '3 h', 210: '3h30',
  240: '4 h', 270: '4h30', 300: '5 h',
  330: '5h30', 360: '6 h',
};

const VALID_DURATIONS = [15, 30, 45, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360];

const COLOR_PRESETS = [
  { label: 'Bleu',     hex: '#3B82F6' },
  { label: 'Violet',   hex: '#8B5CF6' },
  { label: 'Emerald',  hex: '#10B981' },
  { label: 'Ambre',    hex: '#F59E0B' },
  { label: 'Rose',     hex: '#F43F5E' },
  { label: 'Teal',     hex: '#14B8A6' },
  { label: 'Orange',   hex: '#F97316' },
  { label: 'Slate',    hex: '#64748B' },
];

const EMPTY_FORM = { nom: '', durees: [], couleur: '#3B82F6', description: '', prix: {} };

function hex2alpha(hex, a) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}

export default function AdminCategories({ salonDevise }) {
  const showToast = useToast();
  const [categories,    setCategories]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showForm,      setShowForm]      = useState(false);
  const [editingId,     setEditingId]     = useState(null);
  const [form,          setForm]          = useState(EMPTY_FORM);
  const [saving,        setSaving]        = useState(false);
  const [formError,     setFormError]     = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/admin/categories');
      setCategories(res.data.data || res.data || []);
    } catch {
      showToast('Erreur lors du chargement des catégories', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setFormError(''); setShowForm(true); };
  const openEdit   = (cat) => {
    setEditingId(cat._id);
    setForm({ nom: cat.nom || '', durees: cat.durees || [], couleur: cat.couleur || '#3B82F6', description: cat.description || '',
      prix: Object.fromEntries((cat.tarifs || []).map(t => [t.duree, String(t.prix)])) });
    setFormError('');
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); setFormError(''); };

  const toggleDuration = (dur) =>
    setForm(prev => ({
      ...prev,
      durees: prev.durees.includes(dur)
        ? prev.durees.filter(d => d !== dur)
        : [...prev.durees, dur].sort((a, b) => a - b),
    }));

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.nom.trim())       { setFormError('Le nom est obligatoire.');              return; }
    if (form.durees.length === 0) { setFormError('Sélectionnez au moins une durée.');  return; }
    const { prix, ...rest } = form;
    const tarifs = form.durees
      .filter(d => prix[d] !== undefined && String(prix[d]).trim() !== '' && Number.isFinite(Number(prix[d])) && Number(prix[d]) >= 0)
      .map(d => ({ duree: d, prix: Number(prix[d]) }));
    const payload = { ...rest, tarifs };
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/categories/${editingId}`, payload);
        showToast('Catégorie mise à jour !');
      } else {
        await api.post('/admin/categories', payload);
        showToast('Catégorie créée !');
      }
      closeForm();
      fetchCategories();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Une erreur est survenue.');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/admin/categories/${deleteConfirm._id}`);
      showToast(`"${deleteConfirm.nom}" archivée.`);
      setDeleteConfirm(null);
      fetchCategories();
    } catch {
      showToast("Erreur lors de l'archivage", 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-black text-slate-800">Catégories de services</h2>
          <p className="text-sm text-slate-400 font-medium mt-0.5">
            {categories.length} catégorie{categories.length !== 1 ? 's' : ''} configurée{categories.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-2xl font-black text-sm shadow-lg shadow-blue-100 hover:bg-blue-700 transition-colors"
        >
          <Plus size={15} /> Nouvelle catégorie
        </button>
      </div>

      {/* Liste */}
      {categories.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm py-20 text-center">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Tag size={22} className="text-slate-300" />
          </div>
          <p className="text-slate-500 font-bold text-sm">Aucune catégorie pour le moment</p>
          <p className="text-slate-300 text-xs mt-1">Créez votre première catégorie de service.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {categories.map(cat => {
            const color = cat.couleur || '#3B82F6';
            return (
              <div
                key={cat._id}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden group"
              >
                {/* Barre couleur en haut */}
                <div style={{ height: '4px', background: `linear-gradient(90deg, ${color}, ${hex2alpha(color, 0.4)})` }} />

                <div className="p-5">
                  {/* En-tête de la carte */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {/* Icône colorée */}
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                        style={{ background: hex2alpha(color, 0.12) }}
                      >
                        <Tag size={16} style={{ color }} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-black text-slate-800 text-sm leading-tight">{cat.nom}</h3>
                        {cat.description && (
                          <p className="text-xs text-slate-400 font-medium mt-0.5 leading-relaxed line-clamp-2">
                            {cat.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEdit(cat)}
                        aria-label={`Modifier ${cat.nom}`}
                        className="p-2 text-slate-300 hover:text-blue-500 hover:bg-blue-50 rounded-xl transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(cat)}
                        aria-label={`Supprimer ${cat.nom}`}
                        className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Séparateur */}
                  <div className="h-px bg-slate-50 mb-3" />

                  {/* Durées */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Clock size={11} className="text-slate-300 shrink-0" />
                    {(cat.durees || []).map(dur => (
                      <span
                        key={dur}
                        className="px-2.5 py-1 rounded-lg text-[10px] font-black tracking-wide"
                        style={{ background: hex2alpha(color, 0.10), color }}
                      >
                        {DURATION_LABELS[dur] || `${dur} min`}
                        {findTarif(cat, dur) !== null && (
                          <span className="ml-1.5 pl-1.5 border-l opacity-90" style={{ borderColor: hex2alpha(color, 0.3) }}>
                            {formatPrix(findTarif(cat, dur), salonDevise)}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal suppression */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-[2rem] w-full max-w-sm shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="h-1 bg-red-400 w-full" />
            <div className="p-7 flex flex-col items-center gap-5 text-center">
              <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center">
                <Trash2 size={20} className="text-red-500" />
              </div>
              <div>
                <h3 className="text-base font-black text-slate-800">Supprimer ce service ?</h3>
                <p className="text-slate-400 text-sm mt-1.5 leading-relaxed">
                  <span className="font-bold text-slate-700">"{deleteConfirm.nom}"</span> sera définitivement supprimé.
                </p>
              </div>
              <div className="flex gap-3 w-full">
                <button onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-3 rounded-2xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors">
                  Annuler
                </button>
                <button onClick={handleDelete}
                  className="flex-1 py-3 bg-red-500 text-white rounded-2xl font-bold text-sm hover:bg-red-600 transition-colors active:scale-95">
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal formulaire */}
      {showForm && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/30 backdrop-blur-sm" onClick={closeForm}>
          <div
            className="bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] w-full max-w-lg shadow-2xl relative flex flex-col overflow-hidden"
            style={{ maxHeight: '92vh' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Barre couleur dynamique */}
            <div style={{ height: '4px', background: form.couleur, flexShrink: 0 }} />

            {/* En-tête */}
            <div className="px-6 pt-5 pb-0 flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-lg font-black text-slate-800">
                  {editingId ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">* champs obligatoires</p>
              </div>
              <button onClick={closeForm} aria-label="Fermer" className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-slate-100 text-slate-400 transition-colors">
                <X size={15} />
              </button>
            </div>

            {/* Corps scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

              {/* Nom */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Nom *</label>
                <input
                  value={form.nom}
                  onChange={e => setForm({ ...form, nom: e.target.value })}
                  placeholder="Ex : Coiffure femme"
                  className="w-full p-3.5 bg-slate-50 rounded-2xl font-bold text-slate-700 text-sm border border-slate-100 outline-none focus:border-blue-400 transition-colors"
                />
              </div>

              {/* Couleur */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Couleur</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {COLOR_PRESETS.map(c => (
                    <button
                      key={c.hex}
                      type="button"
                      onClick={() => setForm({ ...form, couleur: c.hex })}
                      title={c.label}
                      className="w-8 h-8 rounded-full transition-all"
                      style={{
                        backgroundColor: c.hex,
                        outline: form.couleur === c.hex ? `3px solid ${c.hex}` : '3px solid transparent',
                        outlineOffset: '2px',
                        transform: form.couleur === c.hex ? 'scale(1.15)' : 'scale(1)',
                      }}
                    />
                  ))}
                  <input
                    type="color"
                    value={form.couleur}
                    onChange={e => setForm({ ...form, couleur: e.target.value })}
                    className="w-8 h-8 rounded-full cursor-pointer border-0 bg-transparent"
                    title="Couleur personnalisée"
                  />
                  {/* Aperçu */}
                  <div className="flex items-center gap-2 ml-2 px-3 py-1.5 rounded-xl" style={{ background: hex2alpha(form.couleur, 0.10) }}>
                    <div className="w-3 h-3 rounded-full" style={{ background: form.couleur }} />
                    <span className="text-[10px] font-black uppercase tracking-wide" style={{ color: form.couleur }}>
                      Aperçu
                    </span>
                  </div>
                </div>
              </div>

              {/* Durées */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Durées proposées *</label>
                <div className="flex flex-wrap gap-2">
                  {VALID_DURATIONS.map(dur => {
                    const active = form.durees.includes(dur);
                    return (
                      <button
                        key={dur}
                        type="button"
                        onClick={() => toggleDuration(dur)}
                        className="px-3 py-1.5 rounded-xl text-xs font-bold transition-all"
                        style={{
                          background: active ? form.couleur : 'transparent',
                          color: active ? '#fff' : '#64748B',
                          border: active ? `2px solid ${form.couleur}` : '2px solid #E2E8F0',
                          transform: active ? 'scale(1.04)' : 'scale(1)',
                        }}
                      >
                        {DURATION_LABELS[dur]}
                      </button>
                    );
                  })}
                </div>
                {form.durees.length > 0 && (
                  <p className="text-[10px] font-bold text-slate-400">
                    {form.durees.length} durée{form.durees.length > 1 ? 's' : ''} sélectionnée{form.durees.length > 1 ? 's' : ''}
                  </p>
                )}
              </div>

              {/* Prix par durée */}
              {form.durees.length > 0 && (
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                    Prix par durée <span className="normal-case font-medium">(facultatif)</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {form.durees.map(dur => (
                      <div key={dur} className="flex items-center gap-2 bg-slate-50 rounded-2xl border border-slate-100 pl-3 pr-2 py-1.5 focus-within:border-blue-400 transition-colors">
                        <span className="text-xs font-black shrink-0 w-14" style={{ color: form.couleur }}>{DURATION_LABELS[dur]}</span>
                        <input
                          type="number"
                          min="0"
                          inputMode="decimal"
                          aria-label={`Prix ${DURATION_LABELS[dur]} (${currencySymbol(salonDevise)})`}
                          value={form.prix[dur] ?? ''}
                          onChange={e => setForm({ ...form, prix: { ...form.prix, [dur]: e.target.value } })}
                          placeholder="0"
                          className="flex-1 min-w-0 bg-transparent text-right font-bold text-slate-700 text-sm outline-none py-1.5"
                        />
                        <span className="text-[10px] font-black text-slate-400 shrink-0 w-9">{currencySymbol(salonDevise)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                  Description <span className="normal-case font-medium">(facultatif)</span>
                </label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Courte description du service…"
                  className="w-full p-3.5 bg-slate-50 rounded-2xl font-medium text-slate-700 text-sm border border-slate-100 outline-none focus:border-blue-400 resize-none transition-colors"
                />
              </div>

              {formError && (
                <p className="text-red-600 text-sm font-bold bg-red-50 p-3 rounded-xl border border-red-100">
                  {formError}
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-50 shrink-0">
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full p-4 text-white rounded-2xl font-black text-sm flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                style={{ background: form.couleur }}
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {editingId ? 'Enregistrer les modifications' : 'Créer la catégorie'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
